import type { RefObject } from "react";
import { useState } from "react";
import { Button, IconButton } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { resolvePreviewUrl } from "./helpers";
import type { ImageItem, MoveDirection } from "./types";

const NUDGE_FLAG = "bg_removal_nudge_shown";

type Props = {
  orderedImages: ImageItem[];
  maxImages: number;
  imageLimitReached: boolean;
  isUploading: boolean;
  isSubmitting: boolean;
  disableReorder: boolean;
  uploadError: string | null;
  fileInputRef: RefObject<HTMLInputElement | null>;
  showTips?: boolean;
  onAddLocalImage: (file: File) => void;
  onOpenViewer: (imageId: string) => void;
  onMoveImage: (id: string, direction: MoveDirection) => void;
  onSetAsCover: (id: string) => void;
  onRemoveImage: (id: string) => void;
  onBgRemovalVersionChange: (imageId: string, version: "original" | "clean") => void;
};

function StarIcon() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.8 21l1.2-6.8-5-4.9 6.9-1L12 2z" />
    </svg>
  );
}

function WandIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M15 4V2M15 10V8M19 6h-2M13 6h-2M18 20l-9-9M7 21l-4-4 14-14 4 4z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

/** The design's empty-slot glyph: a padlock over a box. */
function LockBoxIcon({ size = 30 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M8 8V6a4 4 0 0 1 8 0v2" />
      <rect x="5" y="8" width="14" height="13" rx="2.5" />
      <path d="M9 13h6" />
    </svg>
  );
}

/** Small picture glyph used in the mobile Photos card header chip. */
function ImageChipIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <circle cx="9" cy="9" r="1.6" />
      <path d="m21 15-4.5-4.5L7 20" />
    </svg>
  );
}

function LightbulbIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 18h6M10 22h4" />
      <path d="M12 2a7 7 0 0 0-4 12.7c.7.5 1 1.3 1 2.1V18h6v-1.2c0-.8.3-1.6 1-2.1A7 7 0 0 0 12 2z" />
    </svg>
  );
}

type CoverActionButtonProps = {
  onClick: (event: React.MouseEvent) => void;
  ariaLabel: string;
  children: React.ReactNode;
  disabled?: boolean;
};

/** Dark blurred pill button overlaid on the cover (delete / bg-removal). */
function CoverActionButton({
  onClick,
  ariaLabel,
  children,
  disabled = false,
}: CoverActionButtonProps) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onClick(event);
      }}
      className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-[9px] bg-[rgba(20,20,22,0.55)] text-white backdrop-blur-sm transition-colors hover:bg-[rgba(20,20,22,0.72)] disabled:cursor-not-allowed disabled:opacity-60"
    >
      {children}
    </button>
  );
}

/** Centered 96px placeholder tile with the padlock-box glyph (empty slots). */
function CenterPlaceholder({ iconSize }: { iconSize: number }) {
  return (
    <span className="relative z-[1] inline-flex h-24 w-24 items-center justify-center rounded-[22px] bg-[color:var(--surface)] text-[color:var(--text-disabled)] shadow-[0_10px_26px_rgba(0,0,0,0.09)]">
      <LockBoxIcon size={iconSize} />
    </span>
  );
}

/** Thin +-shaped guides behind the empty cover placeholder. */
function Crosshair() {
  return (
    <>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-[color:var(--border-light)]"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-[color:var(--border-light)]"
      />
    </>
  );
}

/** Cover image with a graceful fall-back to the empty-slot placeholder. */
function CoverImage({
  src,
  alt,
  onOpen,
}: {
  src: string | null;
  alt: string;
  onOpen: () => void;
}) {
  const [failed, setFailed] = useState(false);
  if (src && !failed) {
    return (
      <img
        src={src}
        alt={alt}
        onClick={onOpen}
        onError={() => setFailed(true)}
        className="absolute inset-0 h-full w-full cursor-zoom-in object-cover"
      />
    );
  }
  return <CenterPlaceholder iconSize={44} />;
}

/** Non-interactive placeholder that pads the thumbnail grid to a full 2×2. */
function EmptyTile() {
  return (
    <div
      aria-hidden="true"
      className="flex min-h-[120px] items-center justify-center rounded-[13px] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] text-[color:var(--text-disabled)] opacity-70"
    >
      <LockBoxIcon />
    </div>
  );
}

/** Small translucent prev/next control in the cover switcher bar. */
function SwitcherButton({
  onClick,
  ariaLabel,
  children,
}: {
  onClick: () => void;
  ariaLabel: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className="inline-flex h-9 w-9 items-center justify-center rounded-[7px] bg-white/15 text-white transition-colors hover:bg-white/25 lg:h-6 lg:w-6"
    >
      {children}
    </button>
  );
}

function ChevronLeftIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="rtl:rotate-180"
    >
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="rtl:rotate-180"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

type ThumbTileProps = {
  previewUrl: string | null;
  onSelect: () => void;
  onRemove: () => void;
  disableReorder: boolean;
};

function ThumbTile({
  previewUrl,
  onSelect,
  onRemove,
  disableReorder,
}: ThumbTileProps) {
  const t = useTranslations();
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(previewUrl) && !failed;
  return (
    <div className="group relative flex min-h-[120px] items-center justify-center overflow-hidden rounded-[13px] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] text-[color:var(--text-disabled)]">
      {showImage ? (
        <img
          src={previewUrl ?? undefined}
          alt={t(keys.products.imageLabel)}
          onError={() => setFailed(true)}
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <LockBoxIcon />
      )}
      {/* Whole-tile click shows this photo in the big view. */}
      <button
        type="button"
        className="absolute inset-0 cursor-pointer bg-transparent transition-colors hover:bg-black/5"
        aria-label={t(keys.products.imageLabel)}
        onClick={onSelect}
        disabled={disableReorder}
      />
      <button
        type="button"
        aria-label={t(keys.common.delete)}
        onClick={(event) => {
          event.stopPropagation();
          onRemove();
        }}
        className="absolute end-2 top-2 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[rgba(20,20,22,0.55)] text-white backdrop-blur-sm transition-colors hover:bg-[rgba(20,20,22,0.72)] lg:h-[25px] lg:w-[25px]"
      >
        <TrashIcon />
      </button>
    </div>
  );
}

function BgRemovalNudge({
  onDismiss,
  onTryNow,
}: {
  onDismiss: () => void;
  onTryNow: () => void;
}) {
  const t = useTranslations();
  return (
    <div className="flex items-start justify-between gap-3 rounded-xl border border-[color-mix(in_oklab,var(--accent-500)_30%,white)] bg-[color-mix(in_oklab,var(--accent-500)_10%,white)] px-3 py-2.5">
      <div className="flex-1">
        <div className="text-meta font-semibold text-[color:var(--accent-600)]">
          {t(keys.products.bgRemovalNudgeTitle)}
        </div>
        <div className="mt-0.5 text-meta text-[color:var(--text-secondary)]">
          {t(keys.products.bgRemovalNudgeBody)}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button variant="link" size="sm" onClick={onTryNow}>
          {t(keys.products.bgRemovalNudgeCta)}
        </Button>
        <IconButton
          variant="ghost"
          ariaLabel={t(keys.common.close)}
          onClick={onDismiss}
          icon={<span aria-hidden="true">{"✕"}</span>}
        />
      </div>
    </div>
  );
}

/**
 * Hybrid photo editor matching the Edit Product redesign: amber "Quick
 * photo tips" callout, then a 1.4fr / 1fr split — a large cover tile on
 * the left and a 2-col grid of secondary thumbs + dropzone on the right.
 *
 * Wraps all existing ProductImagesForm state (upload, reorder,
 * background removal, viewer, validation) — this is purely a visual
 * replacement for the legacy list-based panel.
 */
export function ProductImagesHybridPanel({
  orderedImages,
  maxImages,
  imageLimitReached,
  isUploading,
  isSubmitting,
  disableReorder,
  uploadError,
  fileInputRef,
  showTips = true,
  onAddLocalImage,
  onOpenViewer,
  onSetAsCover,
  onRemoveImage,
  onBgRemovalVersionChange,
}: Props) {
  const t = useTranslations();
  const isMobile = useIsMobile();
  // Treat the nudge as dismissed when the flag is set (or storage is
  // unavailable), so it never shows in those cases — read lazily once.
  const [nudgeDismissed, setNudgeDismissed] = useState(() => {
    try {
      return Boolean(localStorage.getItem(NUDGE_FLAG));
    } catch {
      return true;
    }
  });

  const hasReadyImage = orderedImages.some(
    (img) => img.kind === "remote" && img.status === "READY" && !img.bgRemoval,
  );
  const showNudge = !nudgeDismissed && hasReadyImage;

  const dismissNudge = () => {
    setNudgeDismissed(true);
    try {
      localStorage.setItem(NUDGE_FLAG, "1");
    } catch {
      // ignore storage errors
    }
  };

  const handleNudgeTryNow = () => {
    dismissNudge();
    const firstEligible = orderedImages.find(
      (img) => img.kind === "remote" && img.status === "READY" && !img.bgRemoval,
    );
    if (firstEligible) {
      onBgRemovalVersionChange(firstEligible.id, "clean");
    }
  };

  const count = orderedImages.length;
  const canAdd = !imageLimitReached;

  // The big tile is an image switcher. We track the shown photo by *id* (not
  // index) so reordering — e.g. "Set as cover" moving it to index 0 — never
  // desyncs the selection. Falls back to the cover when unset or after a delete.
  const [activeId, setActiveId] = useState<string | null>(null);
  const active =
    (activeId ? orderedImages.find((img) => img.id === activeId) : null) ??
    orderedImages[0] ??
    null;
  const safeIndex = active
    ? orderedImages.findIndex((img) => img.id === active.id)
    : 0;
  const activePreviewUrl = active ? resolvePreviewUrl(active) : null;
  const isActiveCover = safeIndex === 0;

  // Thumbnails = every photo except the one shown large, as jump targets.
  const thumbs = orderedImages
    .map((img, index) => ({ img, index }))
    .filter(({ img }) => img.id !== active?.id);
  // Pad the 2×2 so it always reads as a full grid at the cover's height.
  const filledCells = thumbs.length + (canAdd ? 1 : 0);
  const padCells = Math.max(0, 4 - filledCells);

  const goPrev = () => {
    if (count === 0) return;
    setActiveId(orderedImages[(safeIndex - 1 + count) % count].id);
  };
  const goNext = () => {
    if (count === 0) return;
    setActiveId(orderedImages[(safeIndex + 1) % count].id);
  };

  const handleBgRemovalOnActive = () => {
    if (!active) return;
    if (active.kind !== "remote" || active.status !== "READY") return;
    if (active.bgRemoval) return;
    onBgRemovalVersionChange(active.id, "clean");
  };

  // Promote the shown photo to cover. It's tracked by id, so after the reorder
  // it becomes index 0 and the badge flips to "Cover" automatically.
  const handleMakeActiveCover = () => {
    if (!active) return;
    setActiveId(active.id);
    onSetAsCover(active.id);
  };

  // Amber "photo tips" callout. Mobile matches the design: one compact line
  // (small bulb + bold prefix + the tips joined). Desktop keeps the fuller
  // titled bullet list.
  const photoTips = !showTips ? null : isMobile ? (
    <div className="flex items-start gap-2.5 rounded-[13px] border border-[color:var(--tip-br)] bg-[color:var(--tip-bg)] px-3 py-[11px]">
      <span className="mt-px shrink-0 text-[color:var(--tip-fg)] [&_svg]:h-4 [&_svg]:w-4">
        <LightbulbIcon />
      </span>
      <div className="text-[12px] leading-[1.5] text-[color:var(--text-muted)]">
        <span className="font-bold text-[color:var(--tip-fg)]">
          {t(keys.products.imageTipsTitle)}
        </span>{" "}
        —{" "}
        {[
          t(keys.products.imageTipLight),
          t(keys.products.imageTipBackground),
          t(keys.products.imageTipAngles),
        ].join(" · ")}
      </div>
    </div>
  ) : (
    <div className="flex items-start gap-3 rounded-[14px] border border-[color:var(--tip-br)] bg-[color:var(--tip-bg)] px-4 py-3.5">
      <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-[color:var(--tip-br)] bg-[color:var(--surface)] text-[color:var(--tip-fg)]">
        <LightbulbIcon />
      </span>
      <div className="min-w-0">
        <div className="text-[13.5px] font-bold text-[color:var(--tip-fg)]">
          {t(keys.products.imageTipsTitle)}
        </div>
        <ul className="m-0 mt-[7px] flex list-none flex-col gap-1.5 p-0">
          {[
            t(keys.products.imageTipLight),
            t(keys.products.imageTipBackground),
            t(keys.products.imageTipAngles),
          ].map((tip) => (
            <li
              key={tip}
              className="flex items-start gap-2 text-[12.5px] leading-[1.45] text-[color:var(--text-muted)]"
            >
              <span
                aria-hidden="true"
                className="mt-[7px] h-[5px] w-[5px] shrink-0 rounded-full bg-[color:var(--tip-fg)]"
              />
              {tip}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  // Single hidden file input, shared by every add/change affordance across
  // both layouts (both trigger it via fileInputRef).
  const hiddenInput = (
    <input
      type="file"
      accept="image/png,image/jpeg,image/webp"
      className="sr-only"
      disabled={isUploading || isSubmitting || imageLimitReached}
      ref={fileInputRef}
      onChange={(event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (imageLimitReached) return;
        onAddLocalImage(file);
        event.target.value = "";
      }}
    />
  );

  // ── Mobile (<lg): Trioty cover-preview-first photo layout ──────────────────
  // Reuses the same image state + handlers (add/cover/remove/viewer); only the
  // presentation differs from the desktop split grid below.
  if (isMobile) {
    const coverUrl = active ? activePreviewUrl : null;
    return (
      <div
        aria-busy={isUploading ? "true" : undefined}
        className="flex flex-col"
      >
        {hiddenInput}

        {/* Card header: icon chip + "Photos" + N / max count */}
        <div className="flex items-center gap-2.5">
          <span
            aria-hidden="true"
            className="inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[8px] bg-[color:var(--soft2)] text-[color:var(--text-secondary)]"
          >
            <ImageChipIcon />
          </span>
          <span className="text-[13.5px] font-bold tracking-[-0.01em] text-[color:var(--text-primary)]">
            {t(keys.products.imagesTitle)}
          </span>
          <span className="ms-auto text-[11.5px] font-medium tabular-nums text-[color:var(--text-muted)]">
            {count} / {maxImages}
          </span>
        </div>

        {/* Cover preview */}
        <div className="relative mb-[11px] mt-[13px] h-[200px] overflow-hidden rounded-[15px] border border-[color:var(--hair)] bg-[color:var(--soft2)]">
          {active && coverUrl ? (
            <CoverImage
              key={active.id}
              src={coverUrl}
              alt={t(keys.products.imageLabel)}
              onOpen={() => onOpenViewer(active.id)}
            />
          ) : (
            <button
              type="button"
              aria-label={t(keys.products.emptyUploadHint)}
              onClick={() => {
                if (!imageLimitReached) fileInputRef.current?.click();
              }}
              className="absolute inset-0 flex items-center justify-center transition-colors hover:bg-[color:var(--soft)]"
            >
              <Crosshair />
              <span className="relative z-[1] inline-flex h-[78px] w-[78px] items-center justify-center rounded-[18px] bg-[color:var(--canvas)] text-[color:var(--text-disabled)] shadow-[0_10px_26px_rgba(0,0,0,0.09)]">
                <LockBoxIcon size={34} />
              </span>
            </button>
          )}

          {active ? (
            <>
              {isActiveCover ? (
                <span className="absolute start-[11px] top-[11px] inline-flex items-center gap-1 rounded-full bg-[color:var(--text-primary)] px-2.5 py-1 text-[10.5px] font-extrabold text-[color:var(--canvas)]">
                  <StarIcon />
                  {t(keys.products.coverLabel)}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={handleMakeActiveCover}
                  className="absolute start-[11px] top-[11px] inline-flex items-center gap-1 rounded-full bg-[rgba(20,20,22,0.6)] px-2.5 py-1 text-[10.5px] font-extrabold text-white backdrop-blur-sm transition-colors hover:bg-[rgba(20,20,22,0.75)]"
                >
                  <StarIcon />
                  {t(keys.products.setAsCover)}
                </button>
              )}
              <div className="absolute end-[11px] top-[11px] flex items-center gap-1.5">
                <CoverActionButton
                  onClick={() => onRemoveImage(active.id)}
                  ariaLabel={t(keys.common.delete)}
                  disabled={isSubmitting || isUploading}
                >
                  <TrashIcon />
                </CoverActionButton>
              </div>
            </>
          ) : null}
        </div>

        {/* Thumbnail strip + dashed add tile */}
        <div className="flex gap-[9px] overflow-x-auto pb-1">
          {orderedImages.map((img) => {
            const selected = img.id === active?.id;
            const thumbUrl = resolvePreviewUrl(img);
            return (
              <button
                key={img.id}
                type="button"
                onClick={() => setActiveId(img.id)}
                aria-pressed={selected}
                aria-label={t(keys.products.imageLabel)}
                className={[
                  "relative flex h-[62px] w-[62px] shrink-0 items-center justify-center overflow-hidden rounded-[12px] border bg-[color:var(--soft2)] text-[color:var(--text-disabled)]",
                  selected
                    ? "border-[color:var(--text-primary)]"
                    : "border-[color:var(--hair)]",
                ].join(" ")}
              >
                {thumbUrl ? (
                  <img
                    src={thumbUrl}
                    alt=""
                    aria-hidden="true"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <LockBoxIcon size={20} />
                )}
              </button>
            );
          })}
          {canAdd ? (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              aria-label={t(keys.products.addImage)}
              className="flex h-[62px] w-[62px] shrink-0 items-center justify-center rounded-[12px] border-[1.5px] border-dashed border-[color:var(--card-br)] text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--soft)]"
            >
              <PlusIcon />
            </button>
          ) : null}
        </div>

        {uploadError ? (
          <div className="mt-2 text-[11.5px] text-[color:var(--error-500)]">
            {uploadError}
          </div>
        ) : null}
        {isUploading ? (
          <div className="mt-2 text-[11.5px] text-[color:var(--text-muted)]">
            {t(keys.products.uploadingImage)}
          </div>
        ) : null}

        {photoTips ? <div className="mt-[13px]">{photoTips}</div> : null}
      </div>
    );
  }

  return (
    <div
      aria-busy={isUploading ? "true" : undefined}
      className="flex flex-col gap-4"
    >
      {/* Quick photo tips — amber callout */}
      {photoTips}

      {isUploading ? (
        <p className="text-meta text-[color:var(--text-muted)]">
          {t(keys.products.uploadingImage)}
        </p>
      ) : null}
      {uploadError ? (
        <div className="text-meta text-[color:var(--error-500)]">
          {uploadError}
        </div>
      ) : null}

      {showNudge ? (
        <BgRemovalNudge
          onDismiss={dismissNudge}
          onTryNow={handleNudgeTryNow}
        />
      ) : null}

      {/* Single file input, always mounted — both the empty-state dropzone and
          the add tile trigger it, so neither needs to own the <input>. */}
      {hiddenInput}

      {/* Photo grid — big switchable cover + 2×2 thumbnails, matching the design */}
      <div className="grid grid-cols-1 items-stretch gap-[13px] md:grid-cols-[1.35fr_1fr]">
        {/* Cover / active preview */}
        <div className="relative flex min-h-[300px] items-center justify-center overflow-hidden rounded-[16px] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] md:min-h-[330px]">
          {active ? (
            <CoverImage
              key={active.id}
              src={activePreviewUrl}
              alt={t(keys.products.imageLabel)}
              onOpen={() => onOpenViewer(active.id)}
            />
          ) : (
            <button
              type="button"
              aria-label={t(keys.products.emptyUploadHint)}
              onClick={() => {
                if (!imageLimitReached) fileInputRef.current?.click();
              }}
              className="absolute inset-0 flex cursor-pointer items-center justify-center transition-colors hover:bg-[color:var(--hover,var(--surface-alt))]"
            >
              <Crosshair />
              <CenterPlaceholder iconSize={44} />
            </button>
          )}

          {active ? (
            <>
              {isActiveCover ? (
                <span className="absolute start-[13px] top-[13px] inline-flex items-center gap-1.5 rounded-full bg-[color:var(--accent-500)] px-2.5 py-1 text-[11px] font-bold text-[color:var(--on-accent)]">
                  <StarIcon />
                  {t(keys.products.coverLabel)}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={handleMakeActiveCover}
                  className="absolute start-[13px] top-[13px] inline-flex items-center gap-1.5 rounded-full bg-[rgba(20,20,22,0.55)] px-2.5 py-1 text-[11px] font-bold text-white backdrop-blur-sm transition-colors hover:bg-[rgba(20,20,22,0.72)]"
                >
                  <StarIcon />
                  {t(keys.products.setAsCover)}
                </button>
              )}
              <div className="absolute end-[13px] top-[13px] flex gap-1.5">
                {active.kind === "remote" &&
                active.status === "READY" &&
                !active.bgRemoval ? (
                  <CoverActionButton
                    onClick={handleBgRemovalOnActive}
                    ariaLabel={t(keys.products.bgRemovalNudgeCta)}
                  >
                    <WandIcon />
                  </CoverActionButton>
                ) : null}
                <CoverActionButton
                  onClick={() => onRemoveImage(active.id)}
                  ariaLabel={t(keys.common.delete)}
                  disabled={isSubmitting || isUploading}
                >
                  <TrashIcon />
                </CoverActionButton>
              </div>
              {count > 1 ? (
                <div className="absolute inset-x-[13px] bottom-[13px] flex items-center justify-between gap-2 rounded-[11px] bg-[rgba(20,20,22,0.55)] px-3 py-[7px] text-white backdrop-blur-md">
                  <div className="flex items-center gap-[9px]">
                    <SwitcherButton
                      onClick={goPrev}
                      ariaLabel={t(keys.common.previous)}
                    >
                      <ChevronLeftIcon />
                    </SwitcherButton>
                    <span className="text-[11.5px] font-semibold tabular-nums">
                      {safeIndex + 1} / {count}
                    </span>
                    <SwitcherButton
                      onClick={goNext}
                      ariaLabel={t(keys.common.next)}
                    >
                      <ChevronRightIcon />
                    </SwitcherButton>
                  </div>
                  <span className="truncate text-[11px] font-semibold opacity-85">
                    {isActiveCover
                      ? t(keys.products.coverLabel)
                      : t(keys.products.imageLabel)}
                  </span>
                </div>
              ) : null}
            </>
          ) : null}
        </div>

        {/* Thumbnails 2×2 — jump targets + add tile, padded and stretched to the
            cover height so it always reads as a full grid. */}
        <div className="grid grid-cols-2 gap-[13px] [grid-auto-rows:1fr] md:h-full">
          {thumbs.map(({ img }) => (
            <ThumbTile
              key={img.id}
              previewUrl={resolvePreviewUrl(img)}
              onSelect={() => setActiveId(img.id)}
              onRemove={() => onRemoveImage(img.id)}
              disableReorder={disableReorder}
            />
          ))}
          {canAdd ? (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex min-h-[120px] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-[13px] border-[1.5px] border-dashed border-[color:var(--border-light)] text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--hover,var(--surface-alt))]"
            >
              <PlusIcon />
              <span className="px-2 text-center text-[10.5px] font-semibold leading-[1.3]">
                {t(keys.products.imageLimit, { count: maxImages })}
              </span>
            </button>
          ) : null}
          {Array.from({ length: padCells }).map((_, i) => (
            <EmptyTile key={`pad-${i}`} />
          ))}
        </div>
      </div>

      {/* Save hint */}
      <div className="flex items-center gap-2 text-[11.5px] text-[color:var(--text-muted)]">
        <span
          aria-hidden="true"
          className="h-[13px] w-[13px] shrink-0 rounded-full border-[1.5px] border-dashed border-[color:var(--border-light)]"
        />
        <span>{t(keys.products.saveHelper)}</span>
      </div>
    </div>
  );
}
